import logSonoraDatapoint from '../dataLogger/logSonoraDatapoint';
import {
    SonoraFileSuggestion,
    SonoraMeetingTimeSuggestion,
    SonoraNetworkCallStatus,
    SonoraStickyNotesSuggestion,
    SonoraContactSuggestion,
    SonoraViewState,
    UserDataServiceSuggestions,
} from 'owa-editor-sonora-types';
import type PickerPluginViewState from 'owa-editor-picker-plugin/lib/schema/PickerPluginViewState';
import { DataProviderType } from '@augloop-types/honeybee';
import { mutatorAction } from 'satcheljs';

export const setIsSuggesting = mutatorAction(
    'setIsSuggesting',
    (viewState: PickerPluginViewState, isSuggesting: boolean) => {
        // only update viewstate if the value is different.
        if (viewState.isSuggesting != isSuggesting) {
            viewState.isSuggesting = isSuggesting;
        }
    }
);

export const setQueryString = mutatorAction(
    'setQueryString',
    (viewState: SonoraViewState, queryString: string) => {
        if (viewState.queryString !== queryString) {
            viewState.queryString = queryString;
        }
    }
);

export const setAccepted = mutatorAction(
    'setAccepted',
    (viewState: SonoraViewState, accepted: boolean) => {
        if (viewState.accepted != accepted) {
            viewState.accepted = accepted;
        }
    }
);

export const resetInitialViewState = mutatorAction(
    'resetInitialViewState',
    (viewState: SonoraViewState) => {
        viewState.sequenceId++;
        viewState.queryString = '';
        viewState.selectedIndex = 0;
        viewState.searchLatencyInMs = 0;
        viewState.accepted = false;

        resetInlineSearchSuggestions(viewState);

        delete viewState.intentDetectionHintCurrentlyShowing;
        delete viewState.intentDetectionHintAccepted;
    }
);

export const resetSonoraSuggestions = mutatorAction(
    'resetSonoraSuggestions',
    (viewState: SonoraViewState) => {
        resetInlineSearchSuggestions(viewState);
    }
);

// Note: this is a shared function - not a mutatorAction
export const resetInlineSearchSuggestions = (viewState: SonoraViewState) => {
    viewState.results = {
        stickyNotesSuggestions: { suggestions: [], logicalId: undefined, traceId: undefined },
        fileSuggestions: { suggestions: [], logicalId: undefined, traceId: undefined },
        meetingTimeSuggestions: { suggestions: [], logicalId: undefined, traceId: undefined },
        vivaTopicSuggestions: { suggestions: [], logicalId: undefined, traceId: undefined },
        contactSuggestions: { suggestions: [], logicalId: undefined, traceId: undefined },
    };
};

export const shiftSelectedIndex = mutatorAction(
    'shiftSelectedIndex',
    (viewState: SonoraViewState, isIncrement: boolean) => {
        let totalCount: number = Object.values(viewState.results).reduce(
            (count, result) => count + result.suggestions.length,
            0
        );
        if (totalCount == 1) {
            // When there are no suggestions, + 1 to total count as "no results found" will be shown
            totalCount++;
        }
        if (isIncrement) {
            viewState.selectedIndex++;
        } else {
            viewState.selectedIndex--;
        }
        // Reset the index when out of the range to create a loop
        if (viewState.selectedIndex < 0) {
            viewState.selectedIndex = totalCount - 1;
        }
        if (viewState.selectedIndex > totalCount - 1) {
            viewState.selectedIndex = 0;
        }
    }
);

export const updateAllSuggestions = mutatorAction(
    'updateAllSuggestions',
    (
        viewState: SonoraViewState,
        userDataServiceSuggestions: UserDataServiceSuggestions,
        searchLatencyInMs: number,
        updateDp: string
    ) => {
        let suggestionsCount = 0;

        const new_stickyNotesSuggestion: SonoraStickyNotesSuggestion = userDataServiceSuggestions[
            DataProviderType.SubstrateSearchQueryStickynotes
        ] as SonoraStickyNotesSuggestion;

        const new_fileSuggestion: SonoraFileSuggestion = userDataServiceSuggestions[
            DataProviderType.SubstrateSearchSuggestions
        ] as SonoraFileSuggestion;

        const new_meetingTimeSuggestion: SonoraMeetingTimeSuggestion = userDataServiceSuggestions[
            DataProviderType.MeetingTimes
        ] as SonoraMeetingTimeSuggestion;

        const new_vivaTopicSuggestion: SonoraFileSuggestion = userDataServiceSuggestions[
            DataProviderType.VivaTopics
        ] as SonoraFileSuggestion;

        const new_contactSuggestion: SonoraContactSuggestion = userDataServiceSuggestions[
            DataProviderType.ChainedContacts
        ] as SonoraContactSuggestion;

        viewState.searchLatencyInMs = searchLatencyInMs;

        // update stickynotes search results
        if (new_stickyNotesSuggestion?.suggestions?.length > 0) {
            viewState.results.stickyNotesSuggestions = new_stickyNotesSuggestion;
            suggestionsCount += new_stickyNotesSuggestion.suggestions.length;
        } else {
            viewState.results.stickyNotesSuggestions = {
                suggestions: [],
                logicalId: undefined,
                traceId: undefined,
            };
        }

        // update file search results
        if (new_fileSuggestion?.suggestions?.length > 0) {
            viewState.results.fileSuggestions = new_fileSuggestion;
            suggestionsCount += new_fileSuggestion.suggestions.length;
        } else {
            viewState.results.fileSuggestions = {
                suggestions: [],
                logicalId: undefined,
                traceId: undefined,
            };
        }

        // update meeting time search results
        if (new_meetingTimeSuggestion?.suggestions?.length > 0) {
            viewState.results.meetingTimeSuggestions = new_meetingTimeSuggestion;
            suggestionsCount += new_meetingTimeSuggestion.suggestions.length;
        } else {
            viewState.results.meetingTimeSuggestions = {
                suggestions: [],
                logicalId: undefined,
                traceId: undefined,
            };
        }

        // Viva topics (cortex) file suggestions
        if (new_vivaTopicSuggestion?.suggestions?.length > 0) {
            viewState.results.vivaTopicSuggestions = new_vivaTopicSuggestion;
            suggestionsCount += new_vivaTopicSuggestion.suggestions.length;
        } else {
            viewState.results.vivaTopicSuggestions = {
                suggestions: [],
                logicalId: undefined,
                traceId: undefined,
            };
        }

        // update contact search results
        if (new_contactSuggestion?.suggestions?.length > 0) {
            viewState.results.contactSuggestions = new_contactSuggestion;
            suggestionsCount += new_contactSuggestion.suggestions.length;
        } else {
            viewState.results.contactSuggestions = {
                suggestions: [],
                logicalId: undefined,
                traceId: undefined,
            };
        }

        if (viewState.isSuggesting) {
            logSonoraDatapoint(viewState, updateDp, {
                numOfSonoraSuggestionsDisplayed: suggestionsCount,
                numOfSonoraFileSuggestionsDisplayed: new_fileSuggestion?.suggestions?.length ?? 0,
                status: SonoraNetworkCallStatus[viewState.latestNetworkCallStatus],
            });
        }
    }
);

export const updateNetworkCallOnDispatch = mutatorAction(
    'updateNetworkCallOnDispatch',
    (viewState: SonoraViewState) => {
        viewState.latestNetworkCallNumber = viewState.latestNetworkCallNumber + 1;
        viewState.latestNetworkCallStatus = SonoraNetworkCallStatus.Pending;
    }
);

export const updateNetworkCallOnCompletion = mutatorAction(
    'updateNetworkCallOnCompletion',
    (viewState: SonoraViewState) => {
        viewState.latestNetworkCallStatus = SonoraNetworkCallStatus.Completed;
    }
);

export const updateNetworkCallOnTimeout = mutatorAction(
    'updateNetworkCallOnTimeout',
    (viewState: SonoraViewState, isAL: boolean = false) => {
        viewState.latestNetworkCallStatus = isAL
            ? SonoraNetworkCallStatus.ALTimeout
            : SonoraNetworkCallStatus.OWSTimeout;
    }
);
